import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import Dropdown from '../../../../components/Dropdown';
import Input from '../../../../components/Input';
import TextArea from '../../../../components/TextArea';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { Validation } from '../../../../interfaces/common';
import { Task, TaskFormData } from '../../../../interfaces/property-visit.interface';

interface TaskModalProps {
  open: boolean;
  editingTask: Task | null;
  formData: TaskFormData;
  validation: Validation;
  loading: boolean;
  taskDropdownSearchData: { category: string; priority: string; status: string };
  allCategoryOptions: any[];
  categoryOptions: any[];
  allPriorityOptions: any[];
  priorityOptions: any[];
  allStatusOptions: any[];
  statusOptions: any[];
  onClose: () => void;
  onFieldChange: (e: any, name: string) => void;
  onFieldBlur: (e: any, name: string) => void;
  onTaskDropdownSearchChange: (e: any, name: string) => void;
  onSave: () => void;
}

const TaskModal: React.FC<TaskModalProps> = ({
  open, editingTask, formData, validation, loading,
  taskDropdownSearchData,
  allCategoryOptions, categoryOptions, allPriorityOptions, priorityOptions, allStatusOptions, statusOptions,
  onClose, onFieldChange, onFieldBlur, onTaskDropdownSearchChange, onSave,
}) => {
  const { t } = useTranslation();
  const isEdit = !!editingTask;
  const minDate = new Date().toISOString().split('T')[0];

  const fieldError = (name: string) => ({
    error: !!validation.fields.find((f) => f.field === name),
    errorMessage: validation.fields.find((f) => f.field === name)?.message ?? '',
  });

  return (
    <Modal
      open={open}
      maxWidth="md"
      titleClassName="text-start"
      actionClassName="justify-content-start"
      title={isEdit
        ? t('propertyVisits.addEditView.pageTexts.tasks.modal.editTitle')
        : t('propertyVisits.addEditView.pageTexts.tasks.modal.addTitle')}
      onClose={onClose}
      content={
        <form autoComplete="off">
          <div className="row g-6">
            <div className="col-12">
              <div className="form-floating form-floating-outline">
                <TextArea
                  id={VALIDATION_FIELD_MAPPING.taskDescription}
                  name={VALIDATION_FIELD_MAPPING.taskDescription}
                  label={t('propertyVisits.addEditView.pageTexts.tasks.fields.description.label')}
                  placeholder={t('propertyVisits.addEditView.pageTexts.tasks.fields.description.placeholder')}
                  value={formData.description}
                  rows={3}
                  required
                  className="form-control-lg h-100"
                  {...fieldError(VALIDATION_FIELD_MAPPING.taskDescription)}
                  onChange={(e: any) => onFieldChange(e, VALIDATION_FIELD_MAPPING.taskDescription)}
                  onBlur={(e: any) => onFieldBlur(e, VALIDATION_FIELD_MAPPING.taskDescription)}
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  values={formData.category ? [formData.category] : []}
                  required
                  options={(categoryOptions || []).map((c: any) => ({ label: c.name, value: c.id }))}
                  allOptions={(allCategoryOptions || []).map((c: any) => ({ label: c.name, value: c.id }))}
                  id={VALIDATION_FIELD_MAPPING.taskCategory}
                  name={VALIDATION_FIELD_MAPPING.taskCategory}
                  searchText={taskDropdownSearchData.category}
                  onSearch={onTaskDropdownSearchChange}
                  placeholder={t('propertyVisits.addEditView.pageTexts.tasks.fields.category.placeholder')}
                  label={t('propertyVisits.addEditView.pageTexts.tasks.fields.category.label')}
                  {...fieldError(VALIDATION_FIELD_MAPPING.taskCategory)}
                  onChange={onFieldChange}
                  onBlur={onFieldBlur}
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  values={formData.priority ? [formData.priority] : []}
                  required
                  options={(priorityOptions || []).map((p: any) => ({ label: p.name, value: p.id }))}
                  allOptions={(allPriorityOptions || []).map((p: any) => ({ label: p.name, value: p.id }))}
                  id={VALIDATION_FIELD_MAPPING.taskPriority}
                  name={VALIDATION_FIELD_MAPPING.taskPriority}
                  searchText={taskDropdownSearchData.priority}
                  onSearch={onTaskDropdownSearchChange}
                  placeholder={t('propertyVisits.addEditView.pageTexts.tasks.fields.priority.placeholder')}
                  label={t('propertyVisits.addEditView.pageTexts.tasks.fields.priority.label')}
                  {...fieldError(VALIDATION_FIELD_MAPPING.taskPriority)}
                  onChange={onFieldChange}
                  onBlur={onFieldBlur}
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  values={formData.status ? [formData.status] : []}
                  required
                  options={(statusOptions || []).map((s: any) => ({ label: s.name, value: s.id }))}
                  allOptions={(allStatusOptions || []).map((s: any) => ({ label: s.name, value: s.id }))}
                  id={VALIDATION_FIELD_MAPPING.taskStatus}
                  name={VALIDATION_FIELD_MAPPING.taskStatus}
                  searchText={taskDropdownSearchData.status}
                  onSearch={onTaskDropdownSearchChange}
                  placeholder={t('propertyVisits.addEditView.pageTexts.tasks.fields.status.placeholder')}
                  label={t('propertyVisits.addEditView.pageTexts.tasks.fields.status.label')}
                  {...fieldError(VALIDATION_FIELD_MAPPING.taskStatus)}
                  onChange={onFieldChange}
                  onBlur={onFieldBlur}
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-floating form-floating-outline">
                <Input
                  type="date"
                  required
                  id={VALIDATION_FIELD_MAPPING.taskDueDate}
                  name={VALIDATION_FIELD_MAPPING.taskDueDate}
                  label={t('propertyVisits.addEditView.pageTexts.tasks.fields.dueDate.label')}
                  min={minDate}
                  value={formData.due_date}
                  {...fieldError(VALIDATION_FIELD_MAPPING.taskDueDate)}
                  onChange={(e: any) => onFieldChange(e, VALIDATION_FIELD_MAPPING.taskDueDate)}
                  onBlur={(e: any) => onFieldBlur(e, VALIDATION_FIELD_MAPPING.taskDueDate)}
                />
              </div>
            </div>
          </div>
        </form>
      }
      action={
        <>
          <Button
            className="btn btn-primary me-3"
            text={isEdit
              ? t('propertyVisits.addEditView.pageTexts.tasks.modal.updateBtn')
              : t('propertyVisits.addEditView.pageTexts.tasks.modal.saveBtn')}
            onClick={onSave}
            loading={loading}
          />
          <Button
            className="btn btn-outline-secondary me-3"
            text={t('propertyVisits.addEditView.pageTexts.tasks.modal.cancelBtn')}
            onClick={onClose}
            disabled={loading}
          />
        </>
      }
    />
  );
};

export default TaskModal;
